"use client";

import * as React from "react";
import { Box } from "../../components/ui/box";
import { fillOpaqueBackdropForLossy } from "../../lib/canvas";
import { Image } from "../media";
import {
  clampOffset,
  computeCoverLayout,
  computeCropRect,
  type Offset,
} from "./crop-math";
import type { Omittable } from "../../lib/props";

// Gap between the stage edge and the viewport circle — the band where the
// dimmed out-of-crop image context shows.
const CIRCLE_INSET = 16;

export type ImageCropperProps = Omittable<{
  /** Image URL to crop — typically an object URL from a picked file. */
  source: string;
  /** Alt text for the preview image. */
  alt: string;
  /**
   * Zoom factor. 1 = the image cover-fits the viewport circle (shorter image
   * edge exactly fills it); higher values zoom in. Clamp at the call site
   * (e.g. 1–3) — the slider's min/max define the range.
   */
  zoom?: number;
  /** Stage side length in px (the viewport circle is inset 16px). Defaults to 224. */
  size?: number;
  /** React 19 ref-as-prop — exposes {@link ImageCropperHandle}. */
  ref?: React.Ref<ImageCropperHandle>;
}>;

export type GetCroppedBlobOptions = Omittable<{
  /** Output bitmap side length in px. Defaults to 512. */
  outputSize?: number;
  /** Output MIME type. Defaults to 'image/jpeg'. */
  type?: string;
  /** Encoder quality (0–1) for lossy types. Defaults to 0.9. */
  quality?: number;
}>;

export type ImageCropperHandle = {
  /** Renders the region inside the viewport circle to a bitmap and returns it. */
  getCroppedBlob: (opts?: GetCroppedBlobOptions) => Promise<Blob>;
};

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load image for cropping"));
    img.src = url;
  });
}

/**
 * Pan + zoom crop preview. The full image shows on a square stage, dimmed
 * outside the circular crop viewport; dragging repositions the image and
 * `zoom` scales it around its center. `getCroppedBlob()` (via ref) renders
 * exactly the region inside the circle — what you see is what uploads.
 */
export function ImageCropper({
  source,
  alt,
  zoom = 1,
  size = 224,
  ref,
}: ImageCropperProps): React.ReactElement {
  const viewport = size - CIRCLE_INSET * 2;
  const [natural, setNatural] = React.useState<{ w: number; h: number } | null>(
    null,
  );
  const [offset, setOffset] = React.useState<Offset>({ x: 0, y: 0 });
  const dragStart = React.useRef<{
    x: number;
    y: number;
    offset: Offset;
  } | null>(null);

  React.useEffect(() => {
    let live = true;
    setNatural(null);
    setOffset({ x: 0, y: 0 });
    loadImage(source).then(
      (img) => live && setNatural({ w: img.naturalWidth, h: img.naturalHeight }),
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [source]);

  const layout = natural
    ? computeCoverLayout(natural.w, natural.h, viewport, zoom)
    : null;
  // Raw offset state survives zoom changes; render and crop always use the
  // clamped value so zooming out never strands the viewport off-image.
  const shown = layout ? clampOffset(offset, layout) : { x: 0, y: 0 };

  React.useImperativeHandle(
    ref,
    () => ({
      getCroppedBlob: async ({
        outputSize = 512,
        type = "image/jpeg",
        quality = 0.9,
      }: GetCroppedBlobOptions = {}) => {
        const img = await loadImage(source);
        const { sx, sy, side } = computeCropRect(
          img.naturalWidth,
          img.naturalHeight,
          viewport,
          zoom,
          offset,
        );
        const canvas = document.createElement("canvas");
        canvas.width = outputSize;
        canvas.height = outputSize;
        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("Canvas 2D context unavailable");
        fillOpaqueBackdropForLossy(ctx, outputSize, outputSize, type);
        ctx.drawImage(img, sx, sy, side, side, 0, 0, outputSize, outputSize);
        return new Promise<Blob>((resolve, reject) => {
          canvas.toBlob(
            (blob) =>
              blob
                ? resolve(blob)
                : reject(new Error("Failed to encode cropped image")),
            type,
            quality,
          );
        });
      },
    }),
    [source, zoom, offset, viewport],
  );

  // Pointer capture keeps the stage receiving moves after the pointer leaves
  // it; the capture is released on up/cancel.
  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    dragStart.current = { x: e.clientX, y: e.clientY, offset: shown };
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const start = dragStart.current;
    if (!start || !layout) return;
    setOffset(
      clampOffset(
        {
          x: start.offset.x + (e.clientX - start.x),
          y: start.offset.y + (e.clientY - start.y),
        },
        layout,
      ),
    );
  };

  const handlePointerEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    dragStart.current = null;
  };

  return (
    <Box touchAction="none"
      // `position="relative"` is load-bearing: both the image and the crop
      // ring below are absolutely positioned against this stage.
      cursor="grab"
      data-slot="image-cropper"
      // The stage is a recessed well the image sits in, not a raised panel.
      surface="sunken"
      height={size}
      onPointerCancel={handlePointerEnd}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      overflow="hidden"
      position="relative"
      rounded="lg"
      width={size}
    >
      {natural && layout ? (
        <Box
          height={layout.displayedHeight}
          left={size / 2 - layout.displayedWidth / 2 + shown.x}
          position="absolute"
          top={size / 2 - layout.displayedHeight / 2 + shown.y}
          width={layout.displayedWidth}
        >
          <Image alt={alt} draggable={false} fill fit="fill" src={source} />
        </Box>
      ) : null}
      {/* Crop viewport: dims everything outside the circle (same scrim family
          as the dialog backdrop) and draws the ring. The box-shadow spread
          paints the dim; the stage clips it. */}
      <Box pointerEvents="none" rounded="full" borderWidth={2}
        className="border-white/90 shadow-[0_0_0_9999px_rgba(0,0,0,0.55)]"
        height={viewport}
        left={CIRCLE_INSET}
        position="absolute"
        top={CIRCLE_INSET}
        width={viewport}
      />
    </Box>
  );
}
